import { useCallback, useEffect, useState } from 'react';
import betslip from '@gin-betting/betslip';
import { GQL } from '@gin-betting/core';
import { sortBy, toNumber } from 'lodash';

import { DEFAULT_MAX_STAKE } from 'app-constants';
import { usePollingMinStake } from 'hooks';
import features from 'services/feature-manager';
import UserDevice from 'services/user-device';
import { getCurrency } from 'utils';
import { INPUT_VARIANT_COUNT } from '../constants';

const {
    utils: { getSuggests },
} = betslip;

interface Input {
    maxStake: string;
    skip?: boolean;
}

interface Output {
    suggests: number[];
    isDefaultSuggests: boolean;
}

const shouldAddMaxBet = features.isActive(
    features.list.BetslipStakeSuggesterByCurrency
);

export default function useSuggests({ maxStake, skip }: Input): Output {
    const { minStake } = usePollingMinStake();
    const [suggests, setSuggests] = useState<number[] | null>(null);
    const maxBet = maxStake ? +maxStake : DEFAULT_MAX_STAKE;

    useEffect(() => {
        if (skip) {
            return;
        }

        if (maxBet === DEFAULT_MAX_STAKE) {
            setSuggests(null);

            return;
        }

        if (!minStake) return;

        const algorithm = shouldAddMaxBet
            ? GQL.StakeSuggesterAlgorithm.BY_CURRENCY_2
            : UserDevice.getInstance()?.isDeviceMobile()
            ? GQL.StakeSuggesterAlgorithm.BY_CURRENCY
            : GQL.StakeSuggesterAlgorithm.BY_MAX_BET;

        getSuggests({
            maxStake: `${maxBet}`,
            minStake: `${minStake}`,
            count: INPUT_VARIANT_COUNT,
            algorithm,
        })
            .then(s => setSuggests(s.map(i => toNumber(i))))
            .catch(err => {
                console.error(err);
            });
    }, [maxBet, skip, minStake]);

    const getSortedBetVariants = useCallback(() => {
        const { betVariants: bVariants } = getCurrency();

        return [
            ...sortBy(bVariants.filter(i => i < +maxBet)).slice(
                0,
                INPUT_VARIANT_COUNT - 1
            ),
            +maxBet,
        ];
    }, [maxBet]);

    const betVariants = suggests || getSortedBetVariants();

    const suggestsWithMaxStake: typeof suggests =
        maxStake && shouldAddMaxBet ? [...betVariants, maxBet] : betVariants;

    return {
        suggests: suggestsWithMaxStake,
        isDefaultSuggests: !suggests,
    };
}
